import { useBusinessService } from '@ingenico/react-section-utils';
import { useSafeLeakSubscriber } from '@ingenico/react-utils/hooks';
import { useEffect, useMemo, useState } from 'react';
import { distinctUntilChanged, tap } from 'rxjs';
import type { LogicalDeviceShareScreenModal } from '../components/logical-device/logical-device-info/LogicalDeviceInfo.model';
import { ScreenShareService } from '../domain/business/services/screen-share.service';
import { RemoteDesktopStatus } from '../domain/models/remoteDesktop.model';
import { ScreenShareSessionStatus } from '../domain/models/screenShare.model';
import { useDeviceManagementCapability } from './useDeviceManagementCapability.hook';

export interface UseDeviceScreenShareFeatureReturn extends LogicalDeviceShareScreenModal {
  readonly isModalOpen: boolean;
  readonly isScreenShareAvailable: boolean;
  readonly isScreenShareSessionOpened: boolean;
  readonly isDeviceAllowedToOpenScreenShare: boolean;
}

export interface UseDeviceScreenShareFeatureParams {
  readonly isTetraDevice: boolean;
  readonly remoteDesktopSignatureDevicePermission?: RemoteDesktopStatus;
  readonly remoteDesktopSignatureDevicePermissionError?: boolean;
}

export function useDeviceScreenShareFeature({
  remoteDesktopSignatureDevicePermission,
  remoteDesktopSignatureDevicePermissionError,
  isTetraDevice,
}: UseDeviceScreenShareFeatureParams): UseDeviceScreenShareFeatureReturn {
  const canShowScreenShare = useDeviceManagementCapability('logicalDevice.showScreenShare');
  const screenShareService = useBusinessService(ScreenShareService);
  const sessionStatusSubscriber = useSafeLeakSubscriber<ScreenShareSessionStatus>();
  const [sessionStatus, setSessionStatus] = useState<ScreenShareSessionStatus>(() =>
    screenShareService.getCurrentScreenShareSessionStatus(),
  );

  useEffect(() => {
    sessionStatusSubscriber(
      screenShareService.getScreenShareSessionStatus().pipe(
        distinctUntilChanged(),
        tap((status) => setSessionStatus(status)),
      ),
    );
  }, [screenShareService, sessionStatusSubscriber]);

  const isDeviceAllowedToOpenScreenShare = useMemo(
    () =>
      remoteDesktopSignatureDevicePermission === RemoteDesktopStatus.Ready &&
      !remoteDesktopSignatureDevicePermissionError,
    [remoteDesktopSignatureDevicePermission, remoteDesktopSignatureDevicePermissionError],
  );

  const isScreenShareAvailable = useMemo(
    () => Boolean(canShowScreenShare) && !isTetraDevice,
    [canShowScreenShare, isTetraDevice],
  );

  const isScreenShareSessionOpened = useMemo(
    () => [ScreenShareSessionStatus.CONNECTED, ScreenShareSessionStatus.RECONNECTING].includes(sessionStatus),
    [sessionStatus],
  );

  return {
    isDeviceAllowedToOpenScreenShare,
    isModalOpen: sessionStatus !== ScreenShareSessionStatus.DISCONNECTED,
    isScreenShareAvailable,
    isScreenShareSessionOpened,
  };
}
